import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";

import {
  Dialog,
  DialogPanel,
  DialogBackdrop,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from "@headlessui/react";
import Button from "../../components/Button";
import TermsAndConditions from "./components/TermsAndConditions";
import LicenseKeyInput from "./components/LicenseKeyInput";

// import { createCheckoutSession } from "./api/paymentRequest";
import { useNavigate } from "react-router";
import { useTranslation } from "../../i18n/TranslationProvider";

import PaymentProcessing from "./PaymentProcessing";
import { requestPlanActivation } from "./api/paymentRequest"; 


const plans = [
  {
    name: "Basic",
    nameKey: "payment.planBasicName",
    titleKey: "payment.planBasicTitle",
    featureKeys: [
      "payment.featureInstantSupport",
      "payment.featureRosterManagement",
      "payment.featureAthleteLookup",
      "payment.featureUpTo15Users",
    ],
    priceKey: "payment.planBasicPrice",
  },
  {
    name: "Pro",
    nameKey: "payment.planProName",
    titleKey: "payment.planProTitle",
    featureKeys: [
      "payment.featureInstantSupport",
      "payment.featureRosterManagement",
      "payment.featureAthleteLookup",
      "payment.feature16to25Users",
    ],
    priceKey: "payment.planProPrice",
  },
  {
    name: "Unlimited",
    nameKey: "payment.planUnlimitedName",
    titleKey: "payment.planUnlimitedTitle",
    featureKeys: [
      "payment.featureInstantSupport",
      "payment.featureRosterManagement",
      "payment.featureAthleteLookup",
      "payment.featureUnlimitedUsers",
    ],
    priceKey: "payment.planUnlimitedPrice",
  },
];

// Roles that see the original plan-selection / Stripe checkout flow.
// Everyone else only sees the license key input.
const ADMIN_ROLES = ["Administrator", "Athletic Director/Administrator"];

const Payment = () => {
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);
  const { userId, hasPaid, isVerified, userProfile, setIsVerified, isPending, hasRoster, refreshRoster } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const showPlanSelection = ADMIN_ROLES.includes(userProfile?.user_type);

  // Tracks which plan tab is active — needed so checkout actually charges
  // for the plan the admin selected instead of a single fixed price.
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const selectedPlan = plans[selectedPlanIndex];

  // License-key verification path (alternative to Stripe checkout)
  const [licenseEndDate, setLicenseEndDate] = useState("");

  const [timezone, setTimezone] = useState("");
  useEffect(() => {
    const tz = Intl?.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(tz || "America/New_York");
  }, []);

//navigation for paying users (admins)
useEffect(() => {
  if (userId && hasPaid) {
    let landingRoute;

    if (userProfile?.iiss_type === "emr") {
      landingRoute = "/emr-dashboard";
    } else if (!hasRoster) {
      landingRoute = "/add-new-athletes";
    } else {
      landingRoute = "/athletes";
    }

    navigate(landingRoute, { replace: true });
  }
}, [userId, hasPaid, navigate, userProfile, hasRoster]);

  const today = new Date();
  const nextYear = new Date(today);
  nextYear.setFullYear(nextYear.getFullYear() + 1);

  const fromDate = today.toLocaleDateString();
  const toDate = nextYear.toLocaleDateString();

  //navigation for licensed users (coaches)
  const handleVerifiedChange = async (val) => {
    setIsVerified(val);

    if (val) {
      const freshHasRoster = await refreshRoster();

      const landingRoute =
        userProfile?.iiss_type === "emr"
          ? "/emr-dashboard"
          : !freshHasRoster
          ? "/add-new-athletes"
          : "/athletes";

      navigate(landingRoute, { replace: true });
    }
  };
  const handleAccept = async () => {
  try {
    setLoading(true);
    await requestPlanActivation({ userId, timezone, plan: selectedPlan.name });
    setRequested(true);
    setShowTermsAndConditions(false);
  } catch (err) {
    console.error("Error requesting plan:", err);
  } finally {
    setLoading(false);
  }
};

if (requested || isPending) {
  return <PaymentProcessing />;
}
  return (
    <>
      <div className="custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2 ">
        {showPlanSelection ? (
          <>
            <h3 className="text-center text-lg md:text-2xl mb-2">
              {t("payment.choosePlanHeading")}
            </h3>
            <p className="text-center">
              {t("payment.chooseThanksInterest")}
              <br />
              {t("payment.chooseRedirectNote")}
            </p>
            <TabGroup
              className="mt-6"
              selectedIndex={selectedPlanIndex}
              onChange={setSelectedPlanIndex}
            >
              <TabList className="flex justify-between border border-primary rounded-t ">
                {plans.map((plan) => (
                  <Tab
                    key={plan.name}
                    className="w-1/3 text-center py-2 data-selected:bg-primary data-selected:text-white"
                  >
                    <h6>{t(plan.nameKey)}</h6>
                  </Tab>
                ))}
              </TabList>
              <TabPanels className="border border-primary text-sm py-2 px-4 ">
                {plans.map((plan) => (
                  <TabPanel key={plan.name}>
                    <h6 className="text-center text-base mt-2 md:text-2xl mb-2">
                      {t(plan.titleKey)}
                    </h6>
                    <ul className="list-disc list-inside md:text-lg">
                      {plan.featureKeys.map((featureKey) => (
                        <li key={featureKey}>{t(featureKey)}</li>
                      ))}
                    </ul>
                    <p className="mt-4 font-bold md:text-lg">
                      {t(plan.priceKey)}{" "}
                      <span className="font-normal italic">
                        {t("payment.minimumContract")}
                      </span>
                    </p>
                    <div className="flex flex-row gap-10 mt-4">
                      <h6 className="md:text-lg">
                        {t("payment.fromLabel")}{" "}
                        <span className="font-normal">{fromDate}</span>
                      </h6>
                      <h6 className="md:text-lg">
                        {t("payment.toLabel")}{" "}
                        <span className="font-normal">{toDate}</span>
                      </h6>
                    </div>
                  </TabPanel>
                ))}
              </TabPanels>
            </TabGroup>
            <Button
              onClick={() => setShowTermsAndConditions(true)}
              className="w-full mt-4"
            >
              {t("payment.choosePlanButton")}
            </Button>
          </>
        ) : (
          <>
            <h3 className="text-center text-lg md:text-2xl mb-2">
              {t("payment.activateMembershipHeading")}
            </h3>
            <p className="text-center">
              {t("payment.activateMembershipSubtext")}
            </p>
            <p className="text-center text-sm">
              {t("payment.activateMembershipSubtext2")}
            </p>
          </>
        )}

        {!showPlanSelection && (
          <LicenseKeyInput
            userId={userId}
            setVerified={handleVerifiedChange}
            setLicenseEndDate={setLicenseEndDate}
            standalone
          />
        )}
      </div>

      <Dialog
        open={showTermsAndConditions}
        onClose={() => setShowTermsAndConditions(false)}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <div className="fixed inset-0 top-10 flex items-center justify-center p-4">
          <DialogPanel className="max-w-4xl space-y-4 border border-primary rounded-lg shadow-md bg-white p-12">
            <div className="max-h-[50vh] overflow-y-auto ">
              <TermsAndConditions isPaymentStep />
            </div>
            <div className="border-b border-gray-300 w-full my-2" />
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleAccept}
                isLoading={loading}
                className="w-full"
              >
                {t("payment.accept")}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                disabled={loading}
                onClick={() => setShowTermsAndConditions(false)}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

export default Payment;